import React, { useEffect, useState } from "react";
import styles from "./CygnusFormBuilder.module.css";
import { v4 as uuidv4 } from "uuid";
import { FieldProps } from "../InputFieldsController/InputFieldsController.utils";
import {
  CygnusFormBuilderProps,
  FormSettings,
  createDefaultFieldSchema,
} from "./CygnusFormBuilder.utils";
import CygnusFormPageSidebar from "./components/CygnusFormPageSidebar/CygnusFormPageSidebar";
import CygnusFormBuilderToolbar, { THEME_PRESETS } from "./components/CygnusFormBuilderToolbar/CygnusFormBuilderToolbar";
import CustomButton from "../../../pages/reusable_components/custom_button_component/Button";
import CygnusFormCanvas from "./components/CygnusFormCanvas/CygnusFormCanvas";
import LiveSimulationModal from "./components/LiveSimulation/LiveSimulation";
import SystemFieldDrawer from "../CygnusForm/components/SystemFieldDrawer/SystemFieldDrawer";
import {
  ReferenceDataOption,
  SystemField,
} from "../CygnusForm/components/SystemFieldDrawer/SystemFieldDrawer.types";
import {
  getAllSystemFields,
  getFieldCategoryOptions,
} from "../CygnusForm/components/SystemFieldDrawer/SystemFieldDrawer.service";
import {
  isUnmappedSystemField,
  patchFieldWithSystemField,
  toCategoryOptions,
} from "../CygnusForm/components/SystemFieldDrawer/SystemFieldDrawer.utils";

const columnId = "column";

const CygnusFormBuilder: React.FC<CygnusFormBuilderProps> = ({
  initialData,
  updateInitialData,
  fieldOptions,
  onFieldOptionsRefresh,
  templateStatus = "Draft",
}) => {
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(
    initialData.fields[0].id || null
  );
  const [activePageIndex, setActivePageIndex] = useState<number>(0);
  const [showSimulation, setShowSimulation] = useState<boolean>(false);
  const [showSystemFieldDrawer, setShowSystemFieldDrawer] = useState<boolean>(false);
  const [systemFieldDrawerFieldId, setSystemFieldDrawerFieldId] = useState<string>("");
  const [systemFields, setSystemFields] = useState<SystemField[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<ReferenceDataOption[]>([]);
  const [systemFieldDataLoading, setSystemFieldDataLoading] = useState<boolean>(false);

  const loadSystemFieldData = async () => {
    setSystemFieldDataLoading(true);
    try {
      const [allFields, categories] = await Promise.all([
        getAllSystemFields(),
        getFieldCategoryOptions(),
      ]);
      setSystemFields(allFields);
      setCategoryOptions(toCategoryOptions(categories));
      return allFields;
    } finally {
      setSystemFieldDataLoading(false);
    }
  };

  const refreshSystemFields = async () => {
    const allFields = await getAllSystemFields();
    setSystemFields(allFields);
    return allFields;
  };

  useEffect(() => {
    loadSystemFieldData();
  }, []);

  const updateFieldSchema = (id: string | null, patch: Partial<FieldProps>) => {
    if (!id) return;

    const index = initialData.fields.findIndex((f) => f.id === id);
    if (index === -1) return;

    const prev = initialData.fields[index];

    // Merge patch first (DO NOT override value blindly)
    const next: FieldProps = { ...prev, ...patch };

    // If the patch explicitly contains value, allow "", false, 0, etc.
    if ("value" in patch) {
      next.value = patch.value;
    }

    const updatedFields = [...initialData.fields];
    updatedFields[index] = next;

    updateInitialData({ ...initialData, fields: updatedFields });
  };


  const handleAddField = () => {
    const id = uuidv4();
    const newField = createDefaultFieldSchema(id);
    updateInitialData({
      ...initialData,
      fields: [...initialData.fields, newField],
      columns: {
        ...initialData.columns,
        [columnId]: {
          ...initialData.columns[columnId],
          fieldIds: [...initialData.columns[columnId].fieldIds, id],
        },
      },
    });
    setSelectedFieldId(newField.id); // Make sure when new field is created the focus automatically goes to that field
  };

  const handleSystemFieldsResolved = (updatedFields: FieldProps[]) => {
    updateInitialData({
      ...initialData,
      fields: updatedFields,
      columns: {
        ...initialData.columns,
        [columnId]: {
          ...initialData.columns[columnId],
          fieldIds: updatedFields.map((field) => field.id),
        },
      },
    });
  };

  const handleSystemFieldCreated = (systemField: SystemField) => {
    const id = uuidv4();
    const newField = patchFieldWithSystemField(createDefaultFieldSchema(id), systemField);

    updateInitialData({
      ...initialData,
      fields: [...initialData.fields, newField],
      columns: {
        ...initialData.columns,
        [columnId]: {
          ...initialData.columns[columnId],
          fieldIds: [...initialData.columns[columnId].fieldIds, id],
        },
      },
    });
    setSelectedFieldId(id);
  };

  const openSystemFieldDrawer = (fieldId = "") => {
    setSystemFieldDrawerFieldId(fieldId);
    setShowSystemFieldDrawer(true);
  };

  /**
   * Removes an input box from schema and updates state.
   */
  const removeField = (id: string) => {
    updateInitialData({
      ...initialData,
      fields: initialData.fields.filter((field) => field.id !== id),
      columns: {
        ...initialData.columns,
        [columnId]: {
          ...initialData.columns[columnId],
          fieldIds: initialData.columns[columnId].fieldIds.filter((fid) => fid !== id),
        },
      },
    });
  };

  const setPageName = (pageIndex: number, newName: string) => {
    const updatedFields = [...initialData.fields];

    // Identify all the indexes where a new page starts
    const pageBreakFieldIndexes = updatedFields.reduce<number[]>(
      (acc, field, i) => {
        if (i === 0 || field.pageBreak) acc.push(i);
        return acc;
      },
      []
    );

    const pageBreakFieldIndex = pageBreakFieldIndexes[pageIndex];
    if (pageBreakFieldIndex !== undefined) {
      updatedFields[pageBreakFieldIndex] = {
        ...updatedFields[pageBreakFieldIndex],
        pageName: newName,
      };

      updateInitialData({
        ...initialData,
        fields: updatedFields,
      });
    }
  };

  const selectedField =
    initialData.fields.find((f) => f.id === selectedFieldId) || null;

  const pageNames = initialData.fields.reduce<string[]>((acc, field, i) => {
    if (i === 0 || field.pageBreak)
      acc.push(field.pageName ?? `Page ${acc.length + 1}`);
    return acc;
  }, []);

  const handleFieldReorder = (newFieldList: FieldProps[]) => {
    const newData = {
      ...initialData,
      fields: newFieldList,
      columns: {
        ...initialData.columns,
        [columnId]: {
          ...initialData.columns[columnId],
          fieldIds: newFieldList.map((f) => f.id),
        },
      },
    };
    updateInitialData(newData);
  };

  const handleApplyTheme = (presetName: string) => {
    const preset = THEME_PRESETS.find((p) => p.name === presetName);
    if (!preset) return;

    let resolvedSectionStyles = preset.sectionStyles;
    if (presetName === "branded") {
      try {
        const raw = localStorage.getItem("theme");
        const firmTheme = raw ? JSON.parse(raw) : null;
        const c = firmTheme?.colors;
        if (c?.tertiary) {
          const tertiary: string = c.tertiary;
          const primary: string = c.primary ?? "#333333";
          const tintBg = (() => {
            const clean = tertiary.replace("#", "");
            if (clean.length !== 6) return tertiary;
            const r = Math.round(parseInt(clean.slice(0, 2), 16) * 0.15 + 255 * 0.85);
            const g = Math.round(parseInt(clean.slice(2, 4), 16) * 0.15 + 255 * 0.85);
            const b = Math.round(parseInt(clean.slice(4, 6), 16) * 0.15 + 255 * 0.85);
            return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
          })();
          resolvedSectionStyles = {
            backgroundColor: tintBg,
            borderColor: tertiary,
            titleColor: primary,
          };
        }
      } catch {}
    }

    const updatedFields = initialData.fields.map((field) => {
      if (!field.sectionStart) return field;
      return { ...field, sectionStyles: { ...field.sectionStyles, ...resolvedSectionStyles } };
    });
    updateInitialData({
      ...initialData,
      fields: updatedFields,
      formSettings: {
        ...initialData.formSettings,
        themePreset: preset.name,
        fieldVariant: preset.fieldVariant,
        sectionVariant: preset.sectionVariant,
        sectionLayout: preset.sectionLayout,
      },
    });
  };

  return (
    <div className={styles.builderContainer}>
      <LiveSimulationModal
        show={showSimulation}
        onClose={() => setShowSimulation(false)}
        fields={initialData.fields}
        fieldVariant={initialData.formSettings?.fieldVariant}
        sectionVariant={initialData.formSettings?.sectionVariant}
        sectionLayout={initialData.formSettings?.sectionLayout}
        intakeRules={initialData.formSettings?.intakeRules}
      />
      <SystemFieldDrawer
        show={showSystemFieldDrawer}
        fields={initialData.fields}
        initialFieldId={systemFieldDrawerFieldId}
        systemFields={systemFields}
        categoryOptions={categoryOptions}
        fieldDataLoading={systemFieldDataLoading}
        onSystemFieldsRefresh={refreshSystemFields}
        onSystemFieldListChanged={onFieldOptionsRefresh}
        onClose={() => setShowSystemFieldDrawer(false)}
        onFieldsResolved={handleSystemFieldsResolved}
        onSystemFieldCreated={handleSystemFieldCreated}
      />
      <div className={styles.sidebar}>
        <CygnusFormPageSidebar
          fields={initialData.fields}
          pageNames={pageNames}
          activePageIndex={activePageIndex}
          setPageName={setPageName}
          setActivePageIndex={setActivePageIndex}
          updateFieldOrder={handleFieldReorder}
        />
      </div>

      <div className={styles.canvas}>
        <div className={styles.addFieldWrapper}>
          <CustomButton onclick={handleAddField} content={"Add new field"} />
        </div>
        <CygnusFormCanvas
          fields={initialData.fields}
          selectedFieldId={selectedFieldId}
          onSelectField={setSelectedFieldId}
          onResolveSystemField={openSystemFieldDrawer}
          fieldVariant={initialData.formSettings?.fieldVariant}
          sectionVariant={initialData.formSettings?.sectionVariant}
          sectionLayout={initialData.formSettings?.sectionLayout}
        />
      </div>

      <div className={styles.toolbar}>
        <CygnusFormBuilderToolbar
          selectedField={selectedField}
          updateField={(updated) => updateFieldSchema(selectedFieldId, updated)}
          removeField={(fieldId) => removeField(fieldId)}
          templateStatus={templateStatus}
          fieldOptions={fieldOptions}
          onLaunchSimulation={() => setShowSimulation(true)}
          onManageSystemFields={() => openSystemFieldDrawer()}
          unresolvedSystemFieldCount={initialData.fields.filter(isUnmappedSystemField).length}
          formSettings={initialData.formSettings}
          updateFormSettings={(settings: FormSettings) =>
            updateInitialData({ ...initialData, formSettings: settings })
          }
          applyTheme={handleApplyTheme}
        />
      </div>
    </div>
  );
};

export default CygnusFormBuilder;
